import { useState } from 'react'
import { Search, Bell, User, ChevronDown, Settings, CreditCard, HelpCircle, LogOut, Trophy, Ticket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useNavigate } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { useAuth } from '@/hooks/useAuth'
import NotificationsDropdown from '@/components/NotificationsDropdown'

interface Notification {
  id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  read: boolean
  created_at: string
}

interface AppHeaderUserProfile {
  username: string;
  avatar_url?: string;
  victory_points: number;
  field_passes?: number;
}

interface AppHeaderProps {
  userProfile?: AppHeaderUserProfile
  notifications?: Notification[]
  onNotificationRead?: (id: string) => void
}

export function AppHeader({ userProfile, notifications = [], onNotificationRead }: AppHeaderProps) {
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const [searchValue, setSearchValue] = useState('')
  
  const unreadCount = notifications.filter(n => !n.read).length

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read && onNotificationRead) {
      onNotificationRead(notification.id)
    }
  }

  return (
    <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between">
      {/* Left side */}
      <div className="flex items-center gap-4">
        <SidebarTrigger className="h-8 w-8" />
        
        {/* Search */}
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search leagues, players, teams..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="pl-10 bg-background/50"
          />
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-4">
        {/* Victory Points */}
        <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-lg">
          <Trophy className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-primary">
            {userProfile?.victory_points?.toLocaleString() || '1,260'}
          </span>
        </div>

        {/* Field Passes */}
        <div className="flex items-center gap-2 px-3 py-1 bg-accent/10 rounded-lg">
          <Ticket className="h-4 w-4 text-accent" />
          <span className="text-sm font-medium text-accent-foreground">
            {userProfile?.field_passes || '3'}
          </span>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-5 w-5 ml-1 hover:bg-accent/20"
            onClick={() => window.location.href = '/buy-field-pass'}
          >
            <span className="text-accent text-xs font-bold">+</span>
          </Button>
        </div>

        {/* Notifications */}
        <NotificationsDropdown />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 px-3">
              <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                {userProfile?.avatar_url ? (
                  <img 
                    src={userProfile.avatar_url} 
                    alt="Avatar" 
                    className="w-8 h-8 rounded-full object-cover" 
                  />
                ) : (
                  <User className="h-4 w-4" />
                )}
              </div>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{userProfile?.username || 'User'}</p>
              <p className="text-xs text-muted-foreground">Manage your account</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Settings className="mr-2 h-4 w-4" />
              Account Settings
            </DropdownMenuItem>
            <DropdownMenuItem>
              <CreditCard className="mr-2 h-4 w-4" />
              Payment Methods
            </DropdownMenuItem>
            <DropdownMenuItem>
              <HelpCircle className="mr-2 h-4 w-4" />
              Support
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}